import * as Sentry from '@sentry/react'

import { LDClient, LDContext, LDFlagChangeset, LDLogger, initialize } from 'launchdarkly-js-client-sdk'

import { FeatureFlag, NumberFeatureFlag } from './featureFlags'
import { getRuntimeConfig } from 'constants/API/runtimeConfig'
import { logger } from 'utils/logging'

/**
 * LaunchDarkly client and the external store the React hooks read from
 * (ISSUE-2307). LaunchDarkly is the only flag backend — Unleash and its proxy
 * are gone.
 *
 * Deliberately a plain module store rather than React context or the
 * LaunchDarkly React SDK:
 *
 *  - `useSyncExternalStore` is the correct way to read a mutating external
 *    source under concurrent rendering, and it removes any question of side
 *    effects during render.
 *  - the React SDK camelCases flag keys by default, which would silently
 *    rewrite every `FeatureFlag` enum value (`allow-second-map-pin` ->
 *    `allowSecondMapPin`) and read as "flag is off" — the worst failure mode
 *    this subsystem has.
 */

let client: LDClient | undefined

/**
 * Fixed flag values for Storybook and tests, bypassing the network entirely.
 * When set, they win over any client value.
 */
let overrides: Record<string, boolean | number> | undefined

/** Set once we have started, so initialisation stays idempotent. */
let started = false

/**
 * True once flag values are final — the client answered, it failed, or
 * LaunchDarkly is not configured at all. Consumers wait on this to tell "flag
 * is off" apart from "flags have not loaded yet", so every terminal outcome
 * must set it. A state that never resolves is a permanent loading spinner in
 * `RouteSwitch`.
 */
let resolved = false

/** Set when flags could not be loaded. Purely diagnostic; values still read `false`. */
let failure: unknown

/** The context key currently identified, so re-identifying the same user is free. */
let identifiedKey: string | undefined

/** The context key whose identify has settled (answered, failed or timed out), so its values are final. */
let settledKey: string | undefined

/**
 * An identity requested before the client existed.
 *
 * `FeatureFlagProvider` is the outermost provider, and React flushes passive
 * effects child-first, so every consumer's mount effect runs *before* the effect
 * that starts this client. `App.controller` asks to identify in one of those, so
 * the first request always arrives too early. It recovers on the web because
 * Auth0 flips `isAuthenticated` asynchronously and the effect re-runs — but a
 * Propstack embed reports `isAuthenticated: true` synchronously, so if nothing in
 * the dependency array changes afterwards the identity would be dropped for the
 * whole session and no user-targeted rule would apply there.
 */
let pendingUserId: string | undefined

/** Subscribers are the React hooks; notified whenever flags or readiness change. */
const listeners = new Set<() => void>()

const notify = (): void => listeners.forEach(listener => listener())

export type FeatureFlagsStatus = {
  /** True once flag values are final, whether or not LaunchDarkly answered. */
  flagsReady: boolean,
  /** Set when LaunchDarkly could not load flags. Treat as opaque. */
  flagsError: unknown,
}

/** `useSyncExternalStore` requires a referentially stable snapshot. */
let statusSnapshot: FeatureFlagsStatus = { flagsReady: false, flagsError: undefined }

const refreshStatus = (): void => {
  if (statusSnapshot.flagsReady === resolved && statusSnapshot.flagsError === failure) return
  statusSnapshot = { flagsReady: resolved, flagsError: failure }
}

/** Marks flag values final. `error` is diagnostic only — values still read `false`. */
const resolve = (error?: unknown): void => {
  resolved = true
  failure = error
  refreshStatus()
  notify()
}

/**
 * Flags whose evaluation has already been reported. Bounded by the `FeatureFlag`
 * enum, so this cannot grow without limit.
 */
const reportedFlags = new Set<string>()

/**
 * Budget for SDK-originated reports. Deliberately a count and not a set keyed by
 * message: SDK messages interpolate response text and retry delays, so a
 * message-keyed cache both fails to dedupe and grows unbounded.
 */
const SDK_REPORT_BUDGET = 3
let sdkReportsSpent = 0

/**
 * Forwards SDK diagnostics to Sentry.
 *
 * Only once initialisation has resolved. Before that, `signalFailedInit` also
 * routes through here (the SDK falls back to its logger when nothing listens on
 * `error`), and the init handler below already reports that failure with the
 * error object attached — so reporting here as well produced two or three events
 * for one outage, on every page load, fleet-wide.
 *
 * Levels follow the SDK, not intuition: recoverable stream failures — a dropped
 * connection, a blocked `*.launchdarkly.com` — arrive at `warn` (`Stream.js`),
 * and only non-recoverable HTTP statuses use `error`. Routing `warn` anywhere
 * non-reporting therefore discards the common case, which is the one that leaves
 * flag values silently stale and stops kill switches arriving. Safe for quota:
 * the SDK logs once per outage episode and re-arms on reconnect.
 */
const reportSdkDiagnostic = (level: 'error' | 'warn', message: string): void => {
  if (!resolved) return
  if (sdkReportsSpent >= SDK_REPORT_BUDGET) return
  sdkReportsSpent += 1

  logger.warn(`LaunchDarkly SDK ${level}: ${message}`, undefined, {
    context: { component: 'featureFlags', action: 'launchDarklySdk' },
  })
}

const sdkLogger: LDLogger = {
  error: message => reportSdkDiagnostic('error', message),
  warn: message => reportSdkDiagnostic('warn', message),
  info: () => undefined,
  debug: () => undefined,
}

/**
 * Bounds how long the app waits for a first answer. The SDK's initial fetch has
 * no timeout of its own, so without this a black-holed request leaves flags
 * unresolved forever. Seconds, as the SDK expects.
 */
const INIT_TIMEOUT_SECONDS = 5

/**
 * Key for the pre-login context. Explicit rather than letting the SDK generate
 * one: an anonymous context with no key makes the SDK mint a UUID and persist
 * it as `ld:$anonUserId`, which is a durable per-browser identifier.
 *
 * All anonymous traffic therefore shares one key. A percentage rollout would
 * bucket it together; no flag uses one today, and the backend keys anonymous
 * evaluations the same way.
 */
const ANONYMOUS_KEY = 'anonymous'

const anonymousContext: LDContext = { kind: 'user', key: ANONYMOUS_KEY, anonymous: true }

/**
 * Running unconfigured disables ~48 behaviours and both kill switches, so it has
 * to be visible — but it is also the normal state of a preview environment until
 * the ConfigMap value is provisioned, and an event per page load is the quota
 * shape this subsystem has been burned by before.
 *
 * A tag costs nothing and makes every other event from the session attributable.
 * `logger.info` cannot carry it: `reportToSentry` drops the `info` level
 * outright, so even `{ sentry: true }` would be inert.
 */
const logUnconfigured = (reason: string): void => {
  logger.info(`LaunchDarkly is not configured (${reason}); every flag reads false`, undefined, {
    context: { component: 'featureFlags', action: 'initLaunchDarkly' },
  })
  Sentry.setTag('launchDarkly', 'unconfigured')
}

/**
 * Records which flags changed before re-rendering. A breadcrumb rather than an
 * event: with `sendEvents: false` LaunchDarkly holds no client-side impression
 * record either, so without this the only evidence a kill switch was delivered is
 * the behaviour change it causes — but one event per flip per live session would
 * be thousands.
 */
const onFlagsChanged = (changes: LDFlagChangeset = {}): void => {
  const changed = Object.keys(changes)
  if (changed.length) {
    logger.info(`LaunchDarkly flags changed: ${changed.join(', ')}`, changes, {
      context: { component: 'featureFlags', action: 'flagsChanged' },
    })
    Sentry.addBreadcrumb({
      category: 'featureFlags',
      level: 'info',
      message: `LaunchDarkly flags changed: ${changed.join(', ')}`,
    })
  }
  notify()
}

/**
 * Deletes any `ld:*` key left in localStorage.
 *
 * This client passes no `bootstrap` and gives the anonymous context an explicit
 * key, so it never writes one — which makes every such key stale by definition
 * and the sweep safe without an allowlist. An earlier iteration of ISSUE-2307 did
 * write them: `ld:<clientSideId>:<base64 context>` held the whole flag set under a
 * key containing the user's email, and `ld:$anonUserId` was a durable per-browser
 * identifier. Nothing else removes them — `clearAuth0LocalStorage` only matches
 * keys containing `auth0` — so anyone who ran that build still has an email on
 * disk, readable by any script on the origin.
 *
 * Runs on every load rather than once behind a marker: it is a handful of keys,
 * and a returning user who visits an older preview build would otherwise
 * re-acquire them permanently.
 */
const sweepStaleLaunchDarklyStorage = (): void => {
  try {
    // Snapshot the keys first — deleting while walking localStorage by index
    // shifts the indices and silently skips entries.
    const stale = Object.keys(window.localStorage).filter(key => key.startsWith('ld:'))
    if (!stale.length) return

    stale.forEach(key => window.localStorage.removeItem(key))
    logger.info(`Removed ${stale.length} stale LaunchDarkly localStorage entries`, undefined, {
      context: { component: 'featureFlags', action: 'sweepStaleStorage' },
    })
  } catch {
    // Storage can be unavailable or throw outright — Safari private mode, a
    // browser with cookies disabled, or a partner iframe denied storage access.
    // Nothing here is load-bearing, so failing to sweep must not stop init.
  }
}

/**
 * Starts the client. Idempotent, and never throws — a flag backend that fails
 * to start must not take the app with it.
 *
 * Unlike the third-party trackers gated in `bootstrap.tsx`, this runs on embed
 * routes too. LaunchDarkly is application configuration, not analytics: the
 * Propstack embed surfaces are themselves gated on `propstack-integration`
 * (see `RouteSwitch`), so skipping initialisation there would 404 the whole
 * embed.
 */
export const initLaunchDarkly = (): void => {
  if (started) return
  started = true

  // Before anything else, and before the early returns below: the keys exist
  // whether or not LaunchDarkly is configured in this environment.
  sweepStaleLaunchDarklyStorage()

  let clientId: string | undefined
  try {
    clientId = getRuntimeConfig().launchDarklyClientId
  } catch {
    // Runtime config not loaded. Cannot happen in the real bootstrap
    // (`index.tsx` awaits `loadRuntimeConfig()` before importing the app), so
    // this means Storybook/jsdom, or that guarantee broke.
    logUnconfigured('runtime config unavailable')
    resolve()
    return
  }

  if (!clientId) {
    logUnconfigured('no launchDarklyClientId in the runtime config')
    resolve()
    return
  }

  try {
    client = initialize(clientId, anonymousContext, {
      // Live updates. The browser SDK has NO polling mode, so `false` here
      // would freeze flag values for the lifetime of the tab and break every
      // kill switch.
      streaming: true,
      // Sends the context in a REPORT body instead of base64-encoded into the
      // URL path, keeping the user's email out of CDN and proxy access logs.
      // This applies to the flag fetches, which are what carry the context.
      // The stream falls back to LaunchDarkly's ping endpoint (which carries no
      // context) because we do not ship `EventSourcePolyfill`, so each change
      // costs a refetch — an acceptable price at our flag-change rate.
      useReport: true,
      // No evaluation analytics: it would send the user context to
      // LaunchDarkly's Contexts store. Flag-usage insight comes from Code
      // References instead, which needs no PII.
      sendEvents: false,
      diagnosticOptOut: true,
      logger: sdkLogger,
    })

    identifiedKey = ANONYMOUS_KEY

    // Apply any identity requested while the client was still being created.
    if (pendingUserId) {
      const userId = pendingUserId
      pendingUserId = undefined
      identifyLaunchDarkly(userId)
    }

    client.on('change', onFlagsChanged)

    // The timeout below is a plain race that does not mark the client failed, so
    // a slow LaunchDarkly can still succeed afterwards. When it does, the SDK
    // stores the flags without emitting `change` (`FlagStore.setFlags` is a bare
    // assignment) and the stream's first payload then diffs against an identical
    // set — so `initialized` is the only signal that late values arrived.
    // Without it a slow cold load would keep serving `false` behind a stale
    // timeout error: the embed lands on the 404 route and never leaves, and
    // components that re-render for other reasons read the fresh value while
    // their siblings keep the stale one.
    client.on('initialized', () => {
      if (resolved && !failure) return
      resolve()
    })

    const startedAt = performance.now()

    // Resolves when the client is usable, rejects on failure OR on timeout.
    // Using the promise rather than the `ready` event avoids the SDK's habit of
    // emitting `failed` and then `ready` for the same failure, where a naive
    // listener pair reports an outage as healthy.
    client.waitForInitialization(INIT_TIMEOUT_SECONDS)
      .then(() => resolve())
      .catch((error: unknown) => {
        resolve(error ?? new Error('LaunchDarkly initialisation failed'))
        logger.error('LaunchDarkly failed to load flags; all flags fall back to their off state', error, {
          context: {
            component: 'featureFlags',
            action: 'initLaunchDarkly',
            metadata: { waitedMs: Math.round(performance.now() - startedAt) },
          },
        })
      })
  } catch (error) {
    resolve(error ?? new Error('LaunchDarkly client failed to initialize'))
    logger.error('LaunchDarkly client failed to initialize; all flags fall back to their off state', error, {
      context: { component: 'featureFlags', action: 'initLaunchDarkly' },
    })
  }
}

/**
 * Reads a flag. `false` whenever LaunchDarkly is unavailable, unconfigured or
 * does not know the flag — the invariant the whole codebase depends on: OFF
 * must always be the existing/safe behaviour.
 */
export const getFeatureFlagValue = (flag: FeatureFlag): boolean => {
  if (overrides) return overrides[flag] === true
  if (!client) return false
  try {
    return client.variation(flag, false) === true
  } catch (error) {
    // Once per flag: this is the `useSyncExternalStore` getSnapshot, so it runs on
    // every render of every subscriber.
    if (!reportedFlags.has(flag)) {
      reportedFlags.add(flag)
      logger.warn('LaunchDarkly evaluation threw; treating flag as off', error, {
        context: { component: 'featureFlags', action: 'variation', metadata: { flag } },
      })
    }
    return false
  }
}

/**
 * Reads a numeric flag. Falls back to `fallback` whenever LaunchDarkly is
 * unavailable, unconfigured, does not know the flag, or serves something that is
 * not a finite number — the numeric mirror of the boolean invariant: the value
 * compiled into the call site must always be what an unusable flag resolves to.
 */
export const getNumberFeatureFlagValue = (flag: NumberFeatureFlag, fallback: number): number => {
  if (overrides) return typeof overrides[flag] === 'number' ? overrides[flag] as number : fallback
  if (!client) return fallback
  try {
    const value = client.variation(flag, fallback)
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback
  } catch (error) {
    // Once per flag: this is the `useSyncExternalStore` getSnapshot, so it runs on
    // every render of every subscriber.
    if (!reportedFlags.has(flag)) {
      reportedFlags.add(flag)
      logger.warn('LaunchDarkly evaluation threw; using the numeric fallback', error, {
        context: { component: 'featureFlags', action: 'variation', metadata: { flag, fallback } },
      })
    }
    return fallback
  }
}

/** Subscribe to flag/readiness changes. Stable identity, as `useSyncExternalStore` requires. */
export const subscribeToFeatureFlags = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const getFeatureFlagsStatus = (): FeatureFlagsStatus => statusSnapshot

/**
 * Identity the flag provider evaluates targeting rules against. Currently the
 * user's email, matching the `userWithId` rules ported from GitLab. Moving to
 * the platform userId is a coordinated change across web, backend and mobile.
 */
export type FeatureFlagContext = {
  userId?: string,
}

/**
 * Points LaunchDarkly at the current user, or back at the anonymous context
 * when `userId` is absent — which is how logout stops targeting the previous
 * user in a session that never reloads.
 *
 * A no-op when the identity is unchanged: each `identify` refetches every flag.
 *
 * The SDK cannot mark a context `key` private, so identifying does transmit the
 * address; `useReport` keeps it out of URLs and `sendEvents: false` keeps it out
 * of LaunchDarkly's Contexts store.
 */
export const identifyLaunchDarkly = (userId?: string): void => {
  const key = userId || ANONYMOUS_KEY

  if (!client) {
    // Replayed once initialisation finishes — see `pendingUserId`.
    pendingUserId = userId
    return
  }

  if (key === identifiedKey) return

  // Which identity flags are evaluated against, as a zero-quota tag rather than an
  // event. Without it, an Auth0 change that leaves `email` undefined would put the
  // entire user base on the anonymous context — every identity-targeted rule
  // reading false — with nothing to show it happened.
  Sentry.setTag('launchDarklyIdentity', userId ? 'identified' : 'anonymous')

  const previousKey = identifiedKey
  identifiedKey = key
  const context: LDContext = userId ? { kind: 'user', key: userId } : anonymousContext

  // Bounded like initialisation: a caller waiting on `isFlagsReadyFor` must not wait forever.
  const timeout = setTimeout(() => settleIdentity(key), INIT_TIMEOUT_SECONDS * 1000)

  client.identify(context).then(() => {
    clearTimeout(timeout)
    settleIdentity(key)
  }).catch((error: unknown) => {
    clearTimeout(timeout)
    // Roll back, or the dedupe above turns one transient failure into a whole
    // session evaluating email-targeted rules against the wrong context. The
    // driving effect only re-runs on an auth change, so nothing else retries.
    if (identifiedKey === key) identifiedKey = previousKey
    settleIdentity(key)

    logger.warn('LaunchDarkly identify failed; flags continue on the previous context', error, {
      context: { component: 'featureFlags', action: 'identifyLaunchDarkly' },
    })
  })
}

const settleIdentity = (key: string): void => {
  if (settledKey === key) return
  settledKey = key
  notify()
}

/**
 * Whether flag values are final for `userId` (the signed-in user's email, or
 * `undefined` when signed out): flags have loaded and the identify for that
 * user has settled. Before that, a user-targeted flag still reads the anonymous
 * context's value. Always resolves — on a failed or slow identify, on the
 * values already loaded.
 */
export const isFlagsReadyFor = (userId?: string): boolean => {
  if (!resolved) return false
  if (!client || failure !== undefined) return true
  return (userId || ANONYMOUS_KEY) === (settledKey ?? ANONYMOUS_KEY)
}

/**
 * Serves fixed flag values without a client — for Storybook stories and tests.
 *
 * `undefined` keeps any overrides already in effect, so a nested
 * `OfflineFeatureFlagProvider` cannot wipe the values a story set through
 * `parameters.flags`.
 */
export const setFeatureFlagOverrides = (flags?: Record<string, boolean | number>): void => {
  overrides = flags ? { ...flags } : overrides ?? {}
  resolved = true
  refreshStatus()
}

/** Drops overrides so a later test or story can exercise the real client. */
export const clearFeatureFlagOverrides = (): void => {
  overrides = undefined
  // Every flag value changes here, so subscribers have to be told.
  notify()
}

/** Test seam — fully resets module state, closing any live client. */
export const __resetLaunchDarklyForTesting = (): void => {
  client?.close()
  client = undefined
  overrides = undefined
  started = false
  resolved = false
  failure = undefined
  identifiedKey = undefined
  settledKey = undefined
  statusSnapshot = { flagsReady: false, flagsError: undefined }
  pendingUserId = undefined
  listeners.clear()
  reportedFlags.clear()
  sdkReportsSpent = 0
}
